import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '@/utils/dataUtils';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { getFullAddress } from '@/utils/customerUtils';
import Title from '@/components/title';
import OrderItemTable from './order-item-table';

// import CndnItemTable from './components/cndn-item-table';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, notification } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import PrintCndnModal from '@/components/templates/print-cndn-free-form-modal';
import { getCndnTypeName } from '@/helpers/cndnHelper';
import { deleteCndnManual, getCndnManual } from '@/adapters/cndn-manual';
import { ICndnFreeForm } from '@/interfaces/cndnFreeForm';
import CndnItemTable from './cndn-item-table';

const _cndnModal: IDataModal<ICndnFreeForm> = {
  open: false,
};

const _printModal: IDataModal<ICndnFreeForm> = {
  open: false,
};

const ViewCndnFreeform = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { data: cndn } = useQuery<ICndnFreeForm, Error>({
    queryKey: ['get-cndn', id],
    queryFn: async () => await getCndnManual(Number(id)),
  });

  const [deleteModal, setDeleteModal] =
    useState<IDataModal<ICndnFreeForm>>(_cndnModal);
  const [print, setPrint] = useState<IDataModal<ICndnFreeForm>>({
    ..._printModal,
  });
  const onDeleteCndn = async () => {
    try {
      await deleteCndnManual(Number(deleteModal?.id));
      notification.success({
        message: 'ยกเลิกใบเพิ่มหนี้/ลดหนี้สำเร็จ',
      });
      setDeleteModal({ ..._cndnModal });
      navigate(`/cndn-free-form`);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/cndn-free-form`)}
              >
                {`ใบ${cndn && getCndnTypeName(cndn?.type)}`}
              </div>
            ),
          },
          {
            title: `${cndn?.code}`,
          },
        ]}
      />
      {cndn && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`ออเดอร์`}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
            <DetailItem
              label="วันที่ลดหนี้/เพิ่มหนี้"
              detail={formatDate(cndn?.docDate)}
            />
            <DetailItem
              label="ลูกค้า"
              customClass="text-blue-700"
              detail={cndn.customerName}
            />
            <DetailItem
              label="ที่อยู่ลูกค้า"
              detail={getFullAddress(cndn.customer)}
            />
            <DetailItem label="เลขที่ใบสั่งสินค้า (CO)" detail={cndn?.soNo} />
            <DetailItem label="เลขที่ใบแจ้งหนี้ (IV)" detail={cndn?.ivNo} />
            <DetailItem label="เลขที่ใบสั่งซื้อ (PO)" detail={cndn?.poNo} />
            <DetailItem label="BU" detail={cndn?.businessUnit?.name} />
            <DetailItem
              label="เบอร์โทรศัพท์"
              detail={cndn?.customer?.phoneNumber}
            />
            <DetailItem
              label="พนักงานขาย"
              customClass="text-blue-700"
              detail={cndn.salesName}
            />
            <DetailItem
              label="สาเหตุที่ออกใบลดหนี้"
              detail={cndn.remark}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
          </div>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการตามใบแจ้งหนี้`}
          />
          <OrderItemTable
            order={cndn}
            orderItems={
              cndn?.manualCreditDebitNoteItems?.filter(c => !!c.isOrderItem) ||
              []
            }
            showFooter={false}
          />
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการใบ${cndn && getCndnTypeName(cndn?.type)}`}
          />
          {cndn && <CndnItemTable cndn={cndn} />}
          <Divider className="" />
          <div className="flex flex-row justify-between">
            <Button
              type="default"
              size="large"
              className="border-red-500 text-red-700"
              disabled={!cndn?.isActive}
              onClick={() =>
                setDeleteModal({
                  open: true,
                  item: cndn,
                  id: cndn?.id,
                })
              }
            >
              ยกเลิกเอกสาร
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() =>
                setPrint({
                  open: true,
                  id: 'TM26',
                })
              }
            >
              <PrinterOutlined />
              พิมพ์เอกสาร
            </Button>
          </div>
          <Suspense>
            <DeleteConfirmModal
              content={`คุณต้องการลบ '${deleteModal?.item?.code}' ใช่หรือไม่`}
              handleCancel={() => {
                setDeleteModal({ ..._cndnModal });
              }}
              handleOk={() => {
                onDeleteCndn();
              }}
              open={deleteModal?.open}
              title={`ใบเพิ่มหนี้/ลดหนี้ '${deleteModal?.item?.code}'`}
            />
            <PrintCndnModal
              open={print.open}
              handleCancel={function (): void {
                setPrint({
                  ..._printModal,
                });
              }}
              cndn={cndn}
              tms={['TM28']}
            />
          </Suspense>
        </>
      )}
    </Card>
  );
};

export default ViewCndnFreeform;
